package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceOptionsResponse
import ai.jessie.web.onboarding.HTTP_FORBIDDEN
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.problem

// UC-013 outcomes. Everything else keeps the UC-006 tenant-denial presentation unchanged.
internal fun InvoiceRequestFailure.invoiceProblem(saving: Boolean): OnboardingProblem =
    when {
        // A8: no record or reference detail, only the neutral absence.
        status == HTTP_NOT_FOUND -> unavailableInvoiceProblem()
        detail.code == "SETTINGS_MISSING" ->
            OnboardingProblem(
                "Die Rechnungseinstellungen der Organisation fehlen. Es wurde nichts gespeichert. " +
                    "Ein Eigentümer muss die Organisationseinstellungen vervollständigen."
            )
        // A3, A4: the refreshed positions and totals replace the reviewed ones.
        detail.code == "WORK_CHANGED" ->
            OnboardingProblem(
                "Ausgewählte Arbeit ist nicht mehr verrechenbar oder wurde inzwischen geändert. " +
                    "Es wurde nichts gespeichert. Die Positionen und Summen wurden neu berechnet; " +
                    "bitte prüfe sie erneut."
            )
        // A7.
        detail.code == "TOTALS_CHANGED" ->
            OnboardingProblem(
                "Die geprüften Summen stimmen nicht mehr mit den berechneten Positionen überein. " +
                    "Es wurde nichts gespeichert. Die neu berechneten Positionen und Summen werden " +
                    "angezeigt; bitte prüfe sie erneut."
            )
        detail.code == "SELECTION_CHANGED" ->
            OnboardingProblem(
                "Die Auswahl hat sich inzwischen geändert. Es wurde nichts gespeichert. Bitte " +
                    "lass die Positionen neu berechnen und prüfe die Rechnung erneut."
            )
        // A11.
        saving && status == HTTP_FORBIDDEN ->
            OnboardingProblem(
                "Dein Zugriff hat sich geändert. Es wurde nichts gespeichert und keine Arbeit " +
                    "verrechnet."
            )
        // A10: nothing may be presented as created.
        saving && status >= HTTP_SERVER_ERROR ->
            OnboardingProblem(
                "Der Rechnungsentwurf konnte nicht erstellt werden. Es wurde nichts gespeichert " +
                    "und keine Arbeit verrechnet. Deine Angaben bleiben erhalten; bitte versuche " +
                    "es erneut."
            )
        else -> OnboardingRequestFailure(status, detail.code).problem()
    }

internal fun unavailableInvoiceProblem() =
    OnboardingProblem("Nicht verfügbar oder du hast keinen Zugriff.")

internal fun invoiceValidationProblem(problems: Map<String, String>) =
    OnboardingProblem(
        "Bitte korrigiere die markierten Angaben. Es wurde nichts gespeichert.",
        problems.keys.map(::invoiceFieldId),
    )

internal fun uncertainInvoiceProblem() =
    OnboardingProblem(
        "Die Antwort konnte nicht bestätigt werden. Bitte brich ab und lade die Rechnungsliste " +
            "neu, um den gespeicherten Stand zu prüfen."
    )

// The element a problem marks: a details field, the position editor's field or a reviewed line.
internal fun invoiceFieldId(field: String): String {
    val line = LINE_FIELD.matchEntire(field)
    return when {
        line != null -> "invoice-line-${line.groupValues[1]}"
        field.startsWith("line.") -> "invoice-line-${field.removePrefix("line.")}"
        field == "totals" || field == "reviewedTotals" -> "invoice-totals"
        else -> "invoice-$field"
    }
}

internal fun invoiceFieldLabel(field: String): String {
    val line = LINE_FIELD.matchEntire(field)
    if (line != null) {
        val number = line.groupValues[1].toInt() + 1
        val part = line.groupValues[2]
        return if (part.isEmpty()) "Position $number" else "Position $number, ${partLabel(part)}"
    }
    return when (field) {
        "customerId" -> "Kunde"
        "currency" -> "Währung"
        "issueDate" -> "Rechnungsdatum"
        "dueDate" -> "Fälligkeitsdatum"
        "lines" -> "Positionen"
        "totals",
        "reviewedTotals" -> "Summen"
        else -> if (field.startsWith("line.")) partLabel(field.removePrefix("line.")) else "Angabe"
    }
}

private fun partLabel(part: String) =
    when (part) {
        "type" -> "Positionstyp"
        "description" -> "Beschreibung"
        "quantity" -> "Menge"
        "unitPrice" -> "Preis"
        "vatRateId" -> "Mehrwertsteuersatz"
        "projectId" -> "Projekt"
        "positionId" -> "Position"
        "work" -> "Arbeit"
        else ->
            if (part.startsWith("work[")) "Arbeit ${part.removePrefix("work[").removeSuffix("]")}"
            else "Angabe"
    }

internal fun invoiceCorrection(field: String, reason: String) =
    when (reason) {
        "INVALID" -> if (field.endsWith("Date")) "Gib ein gültiges Datum ein." else "Ungültig."
        else -> corrections[reason] ?: "Bitte korrigieren."
    }

private val corrections =
    mapOf(
        "REQUIRED" to "Bitte ausfüllen oder wählen.",
        "UNSUPPORTED" to "Nur CHF, EUR oder USD.",
        "BEFORE_ISSUE_DATE" to "Das Fälligkeitsdatum darf nicht vor dem Rechnungsdatum liegen.",
        "NOT_ELIGIBLE" to "Nicht wählbar: Name oder vollständige Rechnungsadresse fehlt.",
        "TOO_LONG" to "Höchstens 500 Zeichen.",
        "POSITIVE_DECIMAL_REQUIRED" to
            "Gib einen positiven Betrag mit Punkt als Dezimaltrenner ein.",
        "MUST_BE_ABSENT" to "Gehört nicht zu diesem Positionstyp.",
        "NOT_APPLICABLE" to "Kein Mehrwertsteuersatz gilt am Rechnungsdatum.",
        "VAT_NOT_APPLICABLE" to "Kein Mehrwertsteuersatz gilt am Rechnungsdatum.",
        "CUSTOMER_MISMATCH" to "Das Projekt gehört nicht zum gewählten Kunden.",
        "CURRENCY_MISMATCH" to "Nur in der Standardwährung der Organisation verrechenbar.",
        "NOT_BILLABLE" to "Nicht verrechenbar.",
        "POSITION_TYPE_MISMATCH" to "Der Positionstyp passt nicht zur gewählten Position.",
        "ALREADY_BILLED" to "Bereits in einer aktiven Rechnung enthalten.",
        "POSITION_MISMATCH" to "Gehört nicht zu dieser Position.",
        "DUPLICATE" to "Mehrfach ausgewählt.",
        "AMOUNT_TOO_LARGE" to "Der Betrag übersteigt den zulässigen Höchstwert.",
    )

// A3, A4: what the refreshed preview changed, named by the work the member had selected.
internal fun changeMessage(
    field: String,
    reason: String,
    draft: InvoiceDraft,
    options: InvoiceOptionsResponse?,
): String? {
    val line = LINE_FIELD.matchEntire(field) ?: return null
    val number = line.groupValues[1].toInt() + 1
    val selected =
        line.groupValues[2].removePrefix("work[").removeSuffix("]").toIntOrNull()?.let { index ->
            draft.lines.getOrNull(number - 1)?.work?.getOrNull(index)?.id
        }
    val work = options?.workLabel(selected) ?: "die ausgewählte Arbeit"
    return when (reason) {
        "NO_ELIGIBLE_WORK" -> "Position $number wurde entfernt: keine verrechenbare Arbeit mehr."
        "NOT_ELIGIBLE" -> "Position $number: $work ist bereits verrechnet und wurde entfernt."
        "CHANGED" -> "Position $number: $work wurde geändert und neu berechnet."
        else -> null
    }
}

private fun InvoiceOptionsResponse.workLabel(id: String?): String? =
    projects
        .asSequence()
        .flatMap { it.positions }
        .flatMap { it.work }
        .firstOrNull { it.id == id }
        ?.let { "Arbeit vom ${it.workDate}" }

internal const val HTTP_SERVER_ERROR = 500
private const val HTTP_NOT_FOUND = 404
